import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import api from "@/lib/api";
import { STATUS, STATUS_OPTIONS } from "@/lib/constants";

const blank = { slug: "", name: "", category_slug: "", status: "SOB_CONSULTA", image: "", short_description: "", description: "", priority: 3, featured: false, applications: [], highlights: [], specs: [] };

export default function ProductsAdmin() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(blank);

  const load = () => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(blank); setEditing(false); setOpen(true); };
  const openEdit = (p) => { setForm({ ...p, highlights: p.highlights || [] }); setEditing(true); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.slug || !form.name || !form.category_slug) { toast.error("Preencha slug, nome e categoria."); return; }
    const payload = { ...form, priority: Number(form.priority), highlights: typeof form.highlights === "string" ? form.highlights.split("\n").filter(Boolean) : form.highlights };
    try {
      if (editing) await api.put(`/admin/products/${form.slug}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Produto salvo"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
  };

  const del = async (slug) => { if (!window.confirm("Excluir produto?")) return; await api.delete(`/admin/products/${slug}`); load(); toast.success("Excluído"); };

  const quickStatus = async (slug, status) => { await api.patch(`/admin/products/${slug}/status`, { status }); load(); };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white">Produtos</h1>
          <p className="mt-1 text-sm text-[#A1A1AA]">Gerencie produtos e altere o status de disponibilidade.</p>
        </div>
        <Button data-testid="product-new-button" onClick={openNew} className="rounded-none bg-[#FF5500] font-bold hover:bg-[#CC4400]"><Plus className="mr-1 h-4 w-4" /> Novo</Button>
      </div>

      <div className="mt-5 overflow-x-auto border border-[#27272A] bg-[#151618]">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-[#27272A] text-left text-[0.7rem] uppercase tracking-wider text-[#A1A1AA]">
            <th className="px-4 py-3">Produto</th><th className="px-4 py-3">Categoria</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th></tr></thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.slug} data-testid={`product-row-${p.slug}`} className="border-b border-[#27272A] last:border-0 hover:bg-[#1E1F22]">
                <td className="px-4 py-3"><div className="flex items-center gap-3"><img src={p.image} alt="" className="h-9 w-9 object-cover" /><span className="font-medium text-white">{p.name}</span></div></td>
                <td className="px-4 py-3 text-[#A1A1AA]">{categories.find((c) => c.slug === p.category_slug)?.name || p.category_slug}</td>
                <td className="px-4 py-3">
                  <Select value={p.status} onValueChange={(v) => quickStatus(p.slug, v)}>
                    <SelectTrigger data-testid={`product-status-${p.slug}`} className="w-44 rounded-none border-[#27272A] bg-[#0A0A0B] text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#151618] text-white border-[#27272A]">{STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{STATUS[s].label}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3"><div className="flex gap-2">
                  <button onClick={() => openEdit(p)} data-testid={`product-edit-${p.slug}`}><Pencil className="h-4 w-4 text-[#A1A1AA] hover:text-white" /></button>
                  <button onClick={() => del(p.slug)} data-testid={`product-delete-${p.slug}`}><Trash2 className="h-4 w-4 text-[#71717A] hover:text-red-400" /></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto rounded-none border-[#27272A] bg-[#151618] text-white" data-testid="product-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar produto" : "Novo produto"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-eyebrow">Slug *</Label><Input data-testid="product-form-slug" disabled={editing} value={form.slug} onChange={(e) => set("slug", e.target.value)} className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" /></div>
              <div><Label className="label-eyebrow">Nome *</Label><Input data-testid="product-form-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-eyebrow">Categoria *</Label>
                <Select value={form.category_slug} onValueChange={(v) => set("category_slug", v)}>
                  <SelectTrigger data-testid="product-form-category" className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent className="bg-[#151618] text-white border-[#27272A]">{categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="label-eyebrow">Status</Label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger data-testid="product-form-status" className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#151618] text-white border-[#27272A]">{STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{STATUS[s].label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="label-eyebrow">Imagem (URL)</Label><Input data-testid="product-form-image" value={form.image} onChange={(e) => set("image", e.target.value)} className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" /></div>
            <div><Label className="label-eyebrow">Descrição curta</Label><Input data-testid="product-form-short" value={form.short_description} onChange={(e) => set("short_description", e.target.value)} className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" /></div>
            <div><Label className="label-eyebrow">Descrição</Label><Textarea data-testid="product-form-desc" value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" /></div>
            <div><Label className="label-eyebrow">Destaques (um por linha)</Label><Textarea data-testid="product-form-highlights" value={Array.isArray(form.highlights) ? form.highlights.join("\n") : form.highlights} onChange={(e) => set("highlights", e.target.value)} className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-eyebrow">Prioridade (1-3)</Label><Input data-testid="product-form-priority" type="number" value={form.priority} onChange={(e) => set("priority", e.target.value)} className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" /></div>
              <label className="mt-6 flex items-center gap-2 text-sm text-[#A1A1AA]"><input data-testid="product-form-featured" type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} /> Destaque na home</label>
            </div>
            <Button data-testid="product-form-save" onClick={save} className="w-full rounded-none bg-[#FF5500] font-bold hover:bg-[#CC4400]">Salvar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
